/* ==== Batch page is cached on cdn for 1 hours ==== */
import Layout from '@layout/index';
import Batch from '@cont/Batch/Batch';
import {
  fetchBatchData,
  fetchTopicsCovered,
  fetchTestSeries,
  fetchSchedule,
  fetchTrialClasses,
  fetchBatchFaq,
  fetchBatchFeedbacks,
  fetchBatchDemoClasses
} from '@api/fetchers/batch';
import { BATCH_PAGE } from '@constants/segment';
import { useSelector } from 'react-redux';
import { getMe } from '@selectors/auth';
// SWR and API functions
import { SWRConfig } from 'swr';
import { getKey as getBatchDetailsKey } from '@api/hooks/batch/useBatchDetails';
import useBatchSchedule from '@api/hooks/batch/useBatchSchedule';
import { getKey as getBatchTopickey } from '@api/hooks/batch/useBatchTopicsCovered';
import { getKey as getBatchTestSeriesKey } from '@api/hooks/batch/useBatchTestSeries';
import { getKey as getBatchTrailkey } from '@api/hooks/batch/useBatchTrialClasses';
import { getKey as getBatchFeedback } from '@api/hooks/batch/useBatchFeedback';
import { readCookie } from '@utils/cookies';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { getBatchDemoClassesKey } from '@api/hooks/batch/useDemoClasses';

const BatchPage = ({ batchUID, fallbackData }) => {
  const me = useSelector(getMe);
  const { faqs = [] } = fallbackData || {};

  return <Batch batchUID={batchUID} me={me} faqs={faqs} />;
};

const BatchPageWithSWR = ({ goalUID, batchUID, fallbackData }) => {
  const batchDetailsKey = getBatchDetailsKey(batchUID);
  const { key: batchScheduleKey } = useBatchSchedule(batchUID);
  const batchTopicsCoveredKey = getBatchTopickey(batchUID);
  const batchTestSeriesKey = getBatchTestSeriesKey(batchUID);
  const batchTrialClassesKey = getBatchTrailkey(batchUID);
  const batchfeedbackKey = getBatchFeedback(goalUID);
  const batchDemoClassKey = getBatchDemoClassesKey(batchUID);
  const accToken = readCookie('accessToken');
  return (
    <SWRConfig
      value={{
        revalidateOnMount: !!accToken,
        dedupingInterval: 60 * 1000,
        fallback: {
          [batchDetailsKey]: fallbackData?.batchDetails,
          [batchScheduleKey]: fallbackData?.schedule,
          [batchTopicsCoveredKey]: fallbackData?.topicsCovered,
          [batchTestSeriesKey]: fallbackData?.testSeries,
          [batchTrialClassesKey]: fallbackData?.trialClasses,
          [batchfeedbackKey]: fallbackData?.feedback,
          [batchDemoClassKey]: fallbackData?.demoClasses
        }
      }}
    >
      <BatchPage batchUID={batchUID} fallbackData={fallbackData} />
      <OneLinkSmartScript />
    </SWRConfig>
  );
};

BatchPageWithSWR.pageInfo = {
  name: BATCH_PAGE,
  isCMSMeta: true,
  isMWebSupported: true
};

export default BatchPageWithSWR;

export const getStaticProps = async ({ params }) => {
  try {
    const { batchUID } = params;
    const promises = [];
    promises.push(fetchBatchData(batchUID));
    promises.push(fetchTopicsCovered(batchUID));
    promises.push(fetchTestSeries(batchUID));
    promises.push(fetchSchedule(batchUID));
    promises.push(fetchTrialClasses(batchUID));
    promises.push(fetchBatchFaq());
    promises.push(fetchBatchDemoClasses(batchUID));

    const [
      batchDetails,
      topicsCovered,
      testSeries,
      schedule,
      trialClasses,
      faqsResponse,
      demoClasses
    ] = await Promise.all(promises);

    // If batch is not public, but exists, let it render and revalidate it on the client-side.
    if (batchDetails?.errorCode === 'E11002')
      return {
        props: { batchUID, faqs: faqsResponse?.faqs },
        revalidate: 5 // 5 seconds
      };

    const { goal } = batchDetails;
    const feedback = await fetchBatchFeedbacks(goal.uid);

    return {
      props: {
        goalUID: goal.uid,
        batchUID: escape(batchUID),
        fallbackData: {
          batchDetails,
          topicsCovered,
          testSeries,
          schedule,
          trialClasses,
          feedback: feedback || { results: [] },
          faqs: faqsResponse?.faqs,
          demoClasses
        }
      },
      revalidate: 5 // 5 seconds, as this page is cached on cdn
    };
  } catch {
    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

BatchPageWithSWR.Layout = Layout;
BatchPageWithSWR.Wrapper = PageReduxProvider;
